import type {
  offers,
  vehicles,
  Improvement,
  Incentive,
  OfferStatus,
} from '@prisma/client';
import api from '../api';

export type OfferWithVehicle = Omit<offers, 'dealerToken'> & {
  vehicle: vehicles;
};

/** Flat payload the Cars page sends; the backend builds the Prisma create from it. */
export interface CreateOfferRequest {
  dealId: string;
  dealer: string;
  dealerEmail: string;
  dealerPhone?: string | null;
  vehicle: {
    vin: string;
    make: string;
    model: string;
    trim?: string;
    version?: string;
    year: number;
    mileage: number;
    listPrice?: number | null;
    msrp?: number | null;
    photos?: string[];
    sourceId?: string;
    sourceUrl?: string;
    /** Marketcheck `dealer.zip` at offer-creation time — fallback geo center for comp sourcing
     * when the deal itself has no search location (see backend/src/outreach/comps.ts). */
    dealerZip?: string;
  };
}

export type { Improvement, Incentive, OfferStatus };

const offersService = {
  find: async (dealId: string): Promise<OfferWithVehicle[]> => {
    const response = await api.get<OfferWithVehicle[]>('/offers', {
      params: { dealId },
    });
    return response.data;
  },
  get: async (id: string): Promise<OfferWithVehicle> => {
    const response = await api.get<OfferWithVehicle>(`/offers/${id}`);
    return response.data;
  },
  post: async (data: CreateOfferRequest): Promise<OfferWithVehicle> => {
    const response = await api.post<OfferWithVehicle>('/offers', data);
    return response.data;
  },
  /** The only buyer-side write: accept or decline a priced offer. */
  setStatus: async (
    id: string,
    status: 'ACCEPTED' | 'REJECTED'
  ): Promise<OfferWithVehicle> => {
    const response = await api.patch<OfferWithVehicle>(`/offers/${id}`, {
      status,
    });
    return response.data;
  },
};

export default offersService;
